import dayjs from 'dayjs'
import 'dayjs/locale/en' // keep the fallback locale always registered (sync)
import { baseLanguage } from 'src/services/i18n/utils/languageVariants'

/**
 * Lazy per-locale loaders keyed by the app's supported languages.
 *
 * Each value uses a STATIC `import()` path so webpack splits every locale into
 * its own chunk. The previous implementation used a dynamic
 * `require(`dayjs/locale/${lang}`)`, whose template path forces webpack to bundle
 * the entire `dayjs/locale/` directory (~140 locales) into the boot chunk.
 *
 * Only languages the app actually supports are listed; any other value (e.g. the
 * `en-soc` social pseudo-locale, which has no dayjs locale file) falls back to
 * English — identical to the old catch/console.warn behaviour.
 */
const localeLoaders: Record<string, () => Promise<unknown>> = {
  en: () => import('dayjs/locale/en'),
  da: () => import('dayjs/locale/da'),
  th: () => import('dayjs/locale/th'),
  fr: () => import('dayjs/locale/fr'),
  ru: () => import('dayjs/locale/ru'),
  tr: () => import('dayjs/locale/tr'),
  ja: () => import('dayjs/locale/ja'),
  es: () => import('dayjs/locale/es'),
  pt: () => import('dayjs/locale/pt'),
  de: () => import('dayjs/locale/de'),
  zh: () => import('dayjs/locale/zh'),
  ko: () => import('dayjs/locale/ko'),
  vi: () => import('dayjs/locale/vi'),
  ar: () => import('dayjs/locale/ar'),
  hi: () => import('dayjs/locale/hi')
}

/**
 * Loads the dayjs locale for `lang` (lazily) and sets it as the global dayjs
 * locale. Unsupported languages fall back to English. Returns the locale that
 * was actually applied.
 */
export async function loadDayjsLocale(lang: string): Promise<string> {
  // A copy variant (`es-lotobola`) uses its base language's dayjs locale.
  const resolved = localeLoaders[lang] ? lang : baseLanguage(lang)
  const loader = localeLoaders[resolved]
  if (!loader) {
    dayjs.locale('en')
    return 'en'
  }
  await loader()
  dayjs.locale(resolved)
  return resolved
}
